<template>
    <div class="clinic-introduction">
        <!-- セクション1: 左画像、右テキスト -->
        <div class="section" ref="section1">
            <div class="content-wrapper">
                <div class="image-container left-image">
                    <img class="bgImg" src="@/assets/images/clinic_1.jpg" alt="青空クリニック外観">
                </div>
                <div class="text-container right-text">
                    <h3><span class="color-blue">青空クリニック</span>へようこそ</h3>
                    <div class="text-content">
                        <p>当クリニックは、地域の皆様に寄り添う"<span style="color: #1757e0;">地域密着型</span>"クリニックです。</p>
                        <p>一般的な内科診療に加え、生活習慣病、アレルギー疾患など、幅広い診療に対応しています。</p>
                        <p>また、訪問診療や健康診断、予防接種など、地域の皆様のニーズに合わせた医療サービスを提供しています。</p>
                        <p>小さなお子様からご高齢の方まで、ご家族皆様の健康をサポートいたします。</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- セクション2: 右画像、左テキスト -->
        <div class="section" ref="section2">
            <div class="content-wrapper reverse">
                <div class="image-container right-image">
                    <img class="bgImg" src="@/assets/images/clinic_4.jpg" alt="待合室">
                </div>
                <div class="text-container left-text">
                    <h3>快適な<span class="color-blue">待合空間</span></h3>
                    <div class="text-content">
                        <p>待合室は、広々とした<span style="color: #1757e0;">青空</span>をイメージしたデザインで、リラックスできる空間を演出しています。</p>
                        <p>感染対策にも力を入れており、定期的な換気や消毒、空間除菌など、清潔な環境を維持しています。</p>
                        <p>また、患者様同士の距離を確保するため、座席の間に仕切りを設けています。</p>
                        <p>お子様連れの方も安心してご来院いただけるよう、畳コーナーを用意しています。</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- セクション3: 左画像、右テキスト -->
        <div class="section" ref="section3">
            <div class="content-wrapper">
                <div class="image-container left-image">
                    <img class="bgImg" src="@/assets/images/clinic_5.jpg" alt="診療室">
                </div>
                <div class="text-container right-text">
                    <h3>丁寧な<span class="color-blue">診療</span></h3>
                    <div class="text-content">
                        <p>診療は、患者様一人ひとりと向き合い、丁寧な説明と的確な診断を心がけています。</p>
                        <p>電子カルテや検査機器を導入し、迅速かつ効率的な診療を提供しています。</p>
                        <p>地域の皆様の健康を支えるパートナーとして、これからも質の高い医療を提供してまいります。</p>
                        <p>どんな些細なことでも、お気軽にご相談ください。</p>
                    </div>
                </div>
            </div>
        </div>
        <!-- 院長挨拶ボタン -->
        <div class="greeting-button-container">
            <button @click="goToGreeting" class="greeting-button">
                院長挨拶
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: "ClinicIntroduction",
    data() {
        return {
            observer: null
        };
    },
    mounted() {
        this.initScrollAnimation();
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        initScrollAnimation() {
            // Intersection Observer のオプション
            const options = {
                root: null,
                rootMargin: '-10% 0px -10% 0px',
                threshold: 0.1
            };

            // Observer を作成
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            }, options);

            // 各セクションを監視対象に追加
            this.$nextTick(() => {
                const sections = [this.$refs.section1, this.$refs.section2, this.$refs.section3];
                sections.forEach(section => {
                    if (section) {
                        this.observer.observe(section);
                    }
                });
            });
        },
        goToGreeting() {
            this.$router.push('/greeting');
        }
    }
};
</script>

<style scoped>
.clinic-introduction {
    padding: 0;
    overflow-x: hidden;
    overflow-y: hidden;
}

.section {
    min-height: 60vh;
    display: flex;
    align-items: center;
    padding: 40px 20px;
    opacity: 0;
    transform: translateY(50px);
    transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.section.animate-in {
    opacity: 1;
    transform: translateY(0);
}

.content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    align-items: center;
    width: 100%;
}

.content-wrapper.reverse {
    direction: rtl;
}

.content-wrapper.reverse > * {
    direction: ltr;
}

.image-container {
    position: relative;
    overflow: hidden;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    transform: scale(0.9);
    transition: transform 0.8s ease, box-shadow 0.8s ease;
}

.section.animate-in .image-container {
    transform: scale(1);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
}

.bgImg {
    width: 100%;
    height: 400px;
    object-fit: cover;
    transition: transform 0.6s ease;
}

.image-container:hover .bgImg {
    transform: scale(1.05);
}

.text-container {
    padding: 20px;
}

.text-container h3 {
    font-size: clamp(24px, 3vw, 36px);
    font-weight: 700;
    margin-bottom: 30px;
    line-height: 1.3;
    opacity: 0;
    transform: translateX(-30px);
    transition: all 0.6s ease 0.2s;
}

.section.animate-in .text-container h3 {
    opacity: 1;
    transform: translateX(0);
}

.text-content {
    opacity: 0;
    transform: translateX(-30px);
    transition: all 0.6s ease 0.4s;
}

.section.animate-in .text-content {
    opacity: 1;
    transform: translateX(0);
}

.text-container p {
    font-size: clamp(16px, 1.5vw, 18px);
    line-height: 1.8;
    margin-bottom: 20px;
    color: #333;
    font-weight: 400;
}

.text-container p:last-child {
    margin-bottom: 0;
}

.color-blue {
    color: #1757e0;
    font-weight: 600;
    position: relative;
}

.color-blue::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #1757e0, #4a90e2);
    transition: width 0.6s ease;
}

.section.animate-in .color-blue::after {
    width: 100%;
}

/* レスポンシブデザイン */
@media (max-width: 1024px) {
    .content-wrapper {
        gap: 30px;
    }
    
    .section {
        padding: 30px 20px;
        min-height: 50vh;
    }
}

@media (max-width: 768px) {
    .content-wrapper {
        grid-template-columns: 1fr;
        gap: 25px;
        text-align: center;
    }
    
    .content-wrapper.reverse {
        direction: ltr;
    }
    
    .section {
        padding: 25px 15px;
        min-height: auto;
    }
    
    .bgImg {
        height: 280px;
    }
    
    .text-container {
        padding: 15px 10px;
    }
    
    .text-container h3 {
        margin-bottom: 20px;
        transform: translateY(-20px);
    }
    
    .section.animate-in .text-container h3 {
        transform: translateY(0);
    }
    
    .text-container p {
           margin-bottom: 15px;
           letter-spacing: 0.5px;
           transform: translateY(-15px);
       }
     
     .text-container .text-content p {
             font-size: clamp(13px, 3.5vw, 16px) !important;
         }
     
     .section.animate-in .text-container p {
         transform: translateY(0);
     }
    
    .image-container {
        transform: scale(0.95) translateY(-20px);
    }
    
    .section.animate-in .image-container {
        transform: scale(1) translateY(0);
    }
}

@media (max-width: 480px) {
    .section {
        padding: 20px 10px;
    }
    
    .content-wrapper {
        gap: 20px;
    }
    
    .bgImg {
        height: 220px;
    }
    
    .image-container {
        border-radius: 12px;
    }
    
    .text-container {
        padding: 10px 5px;
    }
    
    .text-container h3 {
        margin-bottom: 15px;
        font-size: clamp(20px, 5vw, 28px);
    }
    
    .text-container p {
        font-size: clamp(14px, 4vw, 16px);
        margin-bottom: 12px;
    }
}

/* アニメーション用のキーフレーム */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(-50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slideInRight {
    from {
        opacity: 0;
        transform: translateX(50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* 院長挨拶ボタンのスタイル */
.greeting-button-container {
    text-align: center;
    margin-top: 40px;
    padding-bottom: 40px;
}

.greeting-button {
    display: inline-block;
    position: relative;
    background: #f8f9fa;
    border: 2px solid #007BFF;
    border-radius: 5px;
    padding: 10px 20px;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.7;
    letter-spacing: 0.06em;
    transition: .10s ease-out;
    cursor: pointer;
    -webkit-backface-visibility: hidden;
    -webkit-transform: perspective(0);
    -webkit-transform-style: preserve-3d;
    -webkit-perspective: 1000;
    -webkit-transform: translateZ(0);
    color: #007BFF;
    text-decoration: none;
    font-weight: bold;
}

.greeting-button:hover {
    background-color: #007BFF !important;
    border-color: #007BFF;
    color: #ffffff;
    opacity: 1;
}
</style>